import type { ManufacturingCase } from '@infinitform/platform-core';
import { ManufacturingMethod } from '../models/enums';

/**
 * Shape of the legacy "flat" per-method fields on the editor's `Project`
 * object. These act as a scratch pad for the currently-active manufacturing
 * case — sub-blocks in the optimization panel bind to them as if there were
 * a single manufacturing method. On case switch / save, we sync between this
 * scratch pad and `manufacturingMethods[activeIndex]`.
 */
export interface FlatManufacturingFields {
  manufacturingMethod: ManufacturingMethod;
  millingVector: { xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean };
  drawDirectionVector: { xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean };
  generatePrismatic?: boolean;
  costModel?: ManufacturingCase['costModel'];
  minimumToolSize?: number;
  minimumToolSizeUnit?: 'mm' | 'in';
  ribsDensity?: 'Low' | 'Medium';
  wallThickness?: number | 'auto';
  bottomCutFrac?: number;
  skeletonMethod?: 'Skeleton3D' | 'Skeleton2D';
  maxMillingDepth?: ManufacturingCase['maxMillingDepth'];
  thinWeb?: ManufacturingCase['thinWeb'];
  castingParametric?: boolean;
}

/** Methods with an organic result that can also get a parametric (CADGen) one. */
export function supportsCastingParametric(method: ManufacturingMethod | string | undefined): boolean {
  return method === ManufacturingMethod.DieCasting || method === ManufacturingMethod.InjectionMoldingWithDrawDirection;
}

/** The tool diameter unit, or 'mm' when it is missing or not a known unit. */
export function normalizeToolSizeUnit(unit: unknown): 'mm' | 'in' {
  return unit === 'in' ? 'in' : 'mm';
}

/**
 * Build a new `ManufacturingCase` by reading the flat scratch-pad fields.
 * Only copies fields relevant to the flat `manufacturingMethod` — avoids
 * polluting e.g. an AM case with stale milling vectors.
 *
 * Pure function; does not mutate inputs.
 */
export function captureActiveCaseFromFlat(
  existingCase: ManufacturingCase,
  flat: FlatManufacturingFields,
): ManufacturingCase {
  const method = flat.manufacturingMethod;
  const next: ManufacturingCase = {
    methodId: existingCase.methodId,
    method,
    label: existingCase.label,
    // Not part of the flat scratch pad — there is no per-method flat field
    // for it, so it must be carried over explicitly or every commit
    // (case switch, save) silently wipes the case's materials/overrides.
    variants: existingCase.variants,
  };

  if ([ManufacturingMethod.Milling, ManufacturingMethod.Milling2].includes(method)) {
    next.millingVector = { ...flat.millingVector };
    if (flat.minimumToolSize !== undefined) next.minimumToolSize = flat.minimumToolSize;
    next.minimumToolSizeUnit = normalizeToolSizeUnit(flat.minimumToolSizeUnit);
    if (flat.generatePrismatic !== undefined) next.generatePrismatic = flat.generatePrismatic;
    if (method === ManufacturingMethod.Milling2 && flat.costModel !== undefined) {
      next.costModel = { ...flat.costModel };
    }
    if (method === ManufacturingMethod.Milling2 && flat.maxMillingDepth !== undefined) {
      next.maxMillingDepth = { ...flat.maxMillingDepth };
    }
    if (method === ManufacturingMethod.Milling2 && flat.thinWeb !== undefined) {
      next.thinWeb = { ...flat.thinWeb };
    }
  }

  if (
    [
      ManufacturingMethod.DieCasting,
      ManufacturingMethod.InjectionMoldingWithDrawDirection,
      ManufacturingMethod.Extrusion,
      ManufacturingMethod.InjectionMoldingWithExtrusion,
    ].includes(method)
  ) {
    next.drawDirectionVector = { ...flat.drawDirectionVector };
    if (flat.ribsDensity !== undefined) next.ribsDensity = flat.ribsDensity;
    if (flat.wallThickness !== undefined) next.wallThickness = flat.wallThickness;
    if (flat.bottomCutFrac !== undefined) next.bottomCutFrac = flat.bottomCutFrac;
    if (flat.skeletonMethod !== undefined) next.skeletonMethod = flat.skeletonMethod;
    if (supportsCastingParametric(method) && flat.castingParametric !== undefined) {
      next.castingParametric = flat.castingParametric;
    }
    // Extrusion methods are prismatic-only — the flag must survive the
    // flat↔case round-trip, otherwise the prismatic FEA pass is generated
    // as a TO problem (wrong optimization type + design domain, NURBS crash).
    if (
      method === ManufacturingMethod.Extrusion ||
      method === ManufacturingMethod.InjectionMoldingWithExtrusion
    ) {
      next.generatePrismatic = true;
    }
  }

  return next;
}

/**
 * Project a `ManufacturingCase` into the flat scratch-pad shape so the
 * optimization panel sub-blocks render the active case. Fills in sensible
 * defaults for missing fields (zeroed vectors, method-appropriate booleans)
 * so template bindings never see undefined.
 *
 * Pure function; does not mutate inputs. Callers use the returned object
 * to `Object.assign` onto `this.project`.
 */
export function projectFlatFromCase(c: ManufacturingCase): FlatManufacturingFields {
  const zeroVec = () => ({ xp: false, yp: false, zp: false, xm: false, ym: false, zm: false });

  return {
    manufacturingMethod: c.method as ManufacturingMethod,
    millingVector: c.millingVector ? { ...c.millingVector } : zeroVec(),
    drawDirectionVector: c.drawDirectionVector ? { ...c.drawDirectionVector } : zeroVec(),
    generatePrismatic: c.generatePrismatic,
    costModel: c.costModel ? { ...c.costModel } : undefined,
    minimumToolSize: c.minimumToolSize,
    minimumToolSizeUnit: normalizeToolSizeUnit(c.minimumToolSizeUnit),
    ribsDensity: c.ribsDensity,
    wallThickness: c.wallThickness,
    bottomCutFrac: c.bottomCutFrac,
    skeletonMethod: c.skeletonMethod,
    // Max milling depth belongs to CNC Milling – Prismatic only; any other method clears it.
    maxMillingDepth: c.method === ManufacturingMethod.Milling2 && c.maxMillingDepth ? { ...c.maxMillingDepth } : undefined,
    // Thin web belongs to CNC Milling – Prismatic only; any other method clears it.
    thinWeb: c.method === ManufacturingMethod.Milling2 && c.thinWeb ? { ...c.thinWeb } : undefined,
    castingParametric: supportsCastingParametric(c.method) ? c.castingParametric : undefined,
  };
}

/**
 * Auto-compute a human-readable label for a manufacturing case, used in the
 * case list UI and in RunSummary.currentCase.label.
 *
 *   Additive Manufacturing          → "Additive Manufacturing"
 *   Milling2 with xp+zp set         → "CNC Milling – Prismatic (X+, Z+)"
 *   DieCasting with yp set          → "Die Casting (Y+)"
 *   Extrusion with no vector set    → "Extrusion"
 */
export function computeCaseLabel(c: ManufacturingCase): string {
  const methodLabel = methodDisplayName(c.method as ManufacturingMethod);

  // Collect active directions from whichever vector is relevant to this method.
  const vec = c.millingVector ?? c.drawDirectionVector;
  if (!vec) return methodLabel;

  const dirs: string[] = [];
  if (vec.xp) dirs.push('X+');
  if (vec.xm) dirs.push('X-');
  if (vec.yp) dirs.push('Y+');
  if (vec.ym) dirs.push('Y-');
  if (vec.zp) dirs.push('Z+');
  if (vec.zm) dirs.push('Z-');

  if (dirs.length === 0) return methodLabel;
  return `${methodLabel} (${dirs.join(', ')})`;
}

function methodDisplayName(method: ManufacturingMethod): string {
  switch (method) {
    case ManufacturingMethod.AdditiveManufacturing: return 'Additive Manufacturing';
    case ManufacturingMethod.Milling:               return 'CNC Milling – Organic';
    case ManufacturingMethod.Milling2:              return 'CNC Milling – Prismatic';
    case ManufacturingMethod.DieCasting:            return 'Casting';
    case ManufacturingMethod.InjectionMoldingWithDrawDirection: return 'Casting (Thin Ribs)';
    case ManufacturingMethod.Extrusion:             return 'Extrusion';
    case ManufacturingMethod.InjectionMoldingWithExtrusion:     return 'Extrusion (Thin Ribs)';
    case ManufacturingMethod.InvestmentCasting:     return 'Investment Casting';
    default: return typeof method === 'string' ? method : 'Unknown';
  }
}

/**
 * Construct a brand-new case for `method` with sensible defaults.
 * Used when the user clicks [+ Add Case].
 */
export function makeDefaultCase(methodId: string, method: ManufacturingMethod): ManufacturingCase {
  const c: ManufacturingCase = { methodId, method };

  if ([ManufacturingMethod.Milling, ManufacturingMethod.Milling2].includes(method)) {
    c.millingVector = { xp: false, yp: false, zp: false, xm: false, ym: false, zm: false };
    c.generatePrismatic = method === ManufacturingMethod.Milling2;
    c.minimumToolSize = 6;
    c.minimumToolSizeUnit = 'mm';
    if (method === ManufacturingMethod.Milling2) {
      c.costModel = {
        axesType: '3-axis',
        removalRate: 100,
        machiningCostPerHour: 50,
        setupCost: 20,
      };
    }
  }

  if (
    [
      ManufacturingMethod.DieCasting,
      ManufacturingMethod.InjectionMoldingWithDrawDirection,
      ManufacturingMethod.Extrusion,
      ManufacturingMethod.InjectionMoldingWithExtrusion,
    ].includes(method)
  ) {
    c.drawDirectionVector = { xp: true, yp: false, zp: false, xm: false, ym: false, zm: false };
    // Thin-ribs variants (Casting Thin Ribs and Extrusion Thin Ribs) tie rib density
    // to the optimizer's localvol; default to 'Low' so it is never sent empty.
    if (
      method === ManufacturingMethod.InjectionMoldingWithDrawDirection ||
      method === ManufacturingMethod.InjectionMoldingWithExtrusion
    ) {
      c.ribsDensity = 'Low';
    }
    // Extrusion and Extrusion (Thin Ribs) are prismatic-only — they produce no organic
    // CAD, so CADGen (prismatic) must run or the result has no usable geometry.
    if (
      method === ManufacturingMethod.Extrusion ||
      method === ManufacturingMethod.InjectionMoldingWithExtrusion
    ) {
      c.generatePrismatic = true;
    }
    c.wallThickness = 'auto';
  }

  c.label = computeCaseLabel(c);
  return c;
}

/**
 * Returns true if `method` is allowed to be added again given the existing
 * list of cases. (Guidance #12 originally limited param-less methods — AM —
 * to a single case; lifted per user feedback so AM can be explored with
 * several materials without Advanced-mode variants.)
 */
export function canAddMethod(_method: ManufacturingMethod, _existing: ManufacturingCase[]): boolean {
  // Every method may be added multiple times — AM included, since a second
  // AM case is how a different material is explored without Advanced-mode
  // variants. Duplicate labels are disambiguated as "(1)", "(2)" in the UI.
  return true;
}
